import { Model } from '@nozbe/watermelondb'
import { field, date, readonly, relation, children } from '@nozbe/watermelondb/decorators'

/**
 * Invoice.status values. Must match the backend Prisma enum comment
 * (DRAFT, CONFIRMED, VOIDED, RETURNED) — rows sync verbatim.
 */
export const INVOICE_STATUS = {
    DRAFT: 'DRAFT',
    CONFIRMED: 'CONFIRMED',
    VOIDED: 'VOIDED',
    RETURNED: 'RETURNED',
} as const

/**
 * Every status meaning "this invoice never happened".
 *
 * Desktop shipped a writer that stamped `'VOID'` while every reporting query
 * excluded `'VOIDED'` — so voided invoices were filed as sales. The writer is
 * fixed, but `'VOID'` rows already exist locally *and on the server*, so reads
 * must keep excluding both. Do not narrow this to a single value.
 */
// Mutable `string[]` rather than `readonly` so it can be passed straight to
// WatermelonDB's `Q.notIn`, whose signature rejects readonly arrays.
export const VOIDED_INVOICE_STATUSES: string[] = [INVOICE_STATUS.VOIDED, 'VOID']

/** Invoice.payment_status marker for a voided invoice. */
export const PAYMENT_STATUS_VOID = 'VOID'

export class Invoice extends Model {
    static table = 'invoices'
    static associations = {
        stores: { type: 'belongs_to', key: 'store_id' },
        customers: { type: 'belongs_to', key: 'customer_id' },
        line_items: { type: 'has_many', foreignKey: 'invoice_id' },
        payments: { type: 'has_many', foreignKey: 'invoice_id' },
        e_invoices: { type: 'has_many', foreignKey: 'invoice_id' },
        billing_sessions: { type: 'belongs_to', key: 'billing_session_id' },
    } as const

    @field('store_id') storeId!: string
    @field('customer_id') customerId!: string | null
    @field('created_by_id') createdById!: string
    @field('invoice_number') invoiceNumber!: string
    @field('type') type!: string
    @field('reference_invoice_id') referenceInvoiceId!: string | null
    @field('status') status!: string
    @field('payment_status') paymentStatus!: string
    @field('subtotal') subtotal!: number
    @field('discount_type') discountType!: string | null
    @field('discount_value') discountValue!: number
    @field('discount_amount') discountAmount!: number
    @field('promotion_id') promotionId!: string | null
    @field('taxable_amount') taxableAmount!: number
    @field('cgst_amount') cgstAmount!: number
    @field('sgst_amount') sgstAmount!: number
    @field('igst_amount') igstAmount!: number
    @field('cess_amount') cessAmount!: number
    @field('total_tax') totalTax!: number
    @field('round_off') roundOff!: number
    @field('total_amount') totalAmount!: number
    @field('paid_amount') paidAmount!: number
    @field('due_amount') dueAmount!: number
    @date('due_date') dueDate!: Date | null
    @field('currency') currency!: string
    @field('notes') notes!: string | null
    @field('terms_conditions') termsConditions!: string | null
    @field('is_inter_state') isInterState!: boolean
    @field('is_e_invoiced') isEInvoiced!: boolean
    @field('irn') irn!: string | null
    @field('e_invoice_data') eInvoiceData!: string | null
    @field('pdf_url') pdfUrl!: string | null
    @field('whatsapp_sent') whatsappSent!: boolean
    @field('device_id') deviceId!: string | null
    @field('billing_session_id') billingSessionId!: string | null
    @field('is_emergency') isEmergency!: boolean
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('stores', 'store_id') store: any
    @relation('customers', 'customer_id') customer: any
    @children('line_items') lineItems: any
    @children('payments') payments: any
    @children('e_invoices') eInvoices: any
    @relation('billing_sessions', 'billing_session_id') billingSession: any
}

export class LineItem extends Model {
    static table = 'line_items'
    static associations = {
        invoices: { type: 'belongs_to', key: 'invoice_id' },
        products: { type: 'belongs_to', key: 'product_id' },
    } as const

    @field('invoice_id') invoiceId!: string
    @field('product_id') productId!: string
    @field('variant_id') variantId!: string | null
    @field('batch_id') batchId!: string | null
    @field('description') description!: string | null
    @field('hsn_code') hsnCode!: string | null
    @field('quantity') quantity!: number
    @field('unit') unit!: string
    @field('unit_price') unitPrice!: number
    @field('discount_type') discountType!: string | null
    @field('discount_value') discountValue!: number
    @field('discount_amount') discountAmount!: number
    @field('taxable_amount') taxableAmount!: number
    @field('gst_rate') gstRate!: number
    @field('cess_rate') cessRate!: number
    @field('cgst_amount') cgstAmount!: number
    @field('sgst_amount') sgstAmount!: number
    @field('igst_amount') igstAmount!: number
    @field('cess_amount') cessAmount!: number
    @field('total_amount') totalAmount!: number
    /**
     * Unit cost at the moment of sale. Null on rows written before the column
     * existed — profitability reports must surface that rather than read it as
     * zero, which would show a 100% margin on every historical sale.
     */
    @field('cost_price') costPrice!: number | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('invoices', 'invoice_id') invoice: any
    @relation('products', 'product_id') product: any
}

export class Payment extends Model {
    static table = 'payments'
    static associations = {
        invoices: { type: 'belongs_to', key: 'invoice_id' },
    } as const

    @field('invoice_id') invoiceId!: string
    @field('method') method!: string
    @field('amount') amount!: number
    @field('transaction_ref') transactionRef!: string | null
    @field('status') status!: string
    @date('received_at') receivedAt!: Date
    @field('notes') notes!: string | null
    /** The drawer this money crossed. Null on rows written before v24/v25. */
    @field('billing_session_id') billingSessionId!: string | null
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date
}

export class BillingSession extends Model {
    static table = 'billing_sessions'
    static associations = {
        stores: { type: 'belongs_to', key: 'store_id' },
        users: { type: 'belongs_to', key: 'user_id' },
        invoices: { type: 'has_many', foreignKey: 'billing_session_id' },
    } as const

    @field('store_id') storeId!: string
    @field('device_id') deviceId!: string
    @field('user_id') userId!: string
    @date('session_date') sessionDate!: Date
    @date('opened_at') openedAt!: Date
    @date('closed_at') closedAt!: Date | null
    @field('closed_by_id') closedById!: string | null
    @field('opening_cash') openingCash!: number
    @field('closing_cash') closingCash!: number | null
    @field('expected_cash') expectedCash!: number | null
    @field('variance') variance!: number | null
    @field('status') status!: string
    @field('version') version!: number
    @field('is_synced') isSynced!: boolean
    @field('is_deleted') isDeleted!: boolean
    @readonly @date('created_at') createdAt!: Date
    @readonly @date('updated_at') updatedAt!: Date

    @relation('stores', 'store_id') store: any
    @relation('users', 'user_id') openedBy: any
    @children('invoices') invoices: any
}
